import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Chip, Collapse, Divider, Skeleton, Tooltip, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { callService } from '../services/callService';
import {
  CallDiagnostics as Diagnostics, TranscriptEntry,
} from '../types/diagnostics';

/**
 * La chiamata rigiocata: chi ha detto cosa, quando, e cosa hanno risposto i tool.
 *
 * Sostituisce la vista che la dashboard Vapi offriva prima della ZDR. I tempi
 * non stanno in una schermata separata: ogni risposta dell'assistente porta
 * accanto quanto ha fatto aspettare il chiamante. Il tempo è un dato, non un
 * giudizio: nessuna soglia lo colora.
 * Così «perché è stata lenta» si risponde guardando *dove* nel dialogo, invece
 * che confrontando un grafico con una trascrizione.
 */

const tabular = { fontVariantNumeric: 'tabular-nums' as const };

const clock = (ms: number | null) =>
  ms == null ? '' : new Date(ms).toLocaleTimeString('it-IT', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });

const offset = (ms: number | null) => {
  if (ms == null) return '';
  const s = Math.max(ms, 0) / 1000;
  const m = Math.floor(s / 60);
  const sec = (s % 60).toFixed(2).padStart(m > 0 ? 5 : 1, '0').replace('.', ',');
  return `+${m > 0 ? `${m}:` : ''}${sec} s`;
};

const secs = (ms: number | null | undefined) =>
  ms == null ? '—' : `${(ms / 1000).toFixed(2).replace('.', ',')} s`;

/** Riga dei tempi sotto ogni bolla: ora assoluta e scarto dall'inizio. */
function Stamp({
  entry, align, children,
}: { entry: TranscriptEntry; align: 'left' | 'right'; children?: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, mt: 0.5,
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
      }}
    >
      <Typography variant="caption" sx={{ ...tabular, color: 'text.secondary' }}>
        {clock(entry.time_ms)} <Box component="span" sx={{ color: 'text.disabled' }}>{offset(entry.at_ms)}</Box>
      </Typography>
      {children}
    </Box>
  );
}

function ToolCard({ entry }: { entry: Extract<TranscriptEntry, { kind: 'tool' }> }) {
  const [open, setOpen] = useState(false);
  const failed = !!entry.failure;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
      <Box
        sx={(theme) => ({
          width: '100%', maxWidth: 620, borderRadius: 2, overflow: 'hidden',
          border: 1,
          borderColor: alpha(theme.palette[failed ? 'error' : 'success'].main, 0.35),
          bgcolor: alpha(theme.palette[failed ? 'error' : 'success'].main, 0.05),
        })}
      >
        <Box
          component="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.25, width: '100%',
            px: 1.75, py: 1.25, border: 0, bgcolor: 'transparent', cursor: 'pointer',
            font: 'inherit', textAlign: 'left',
            '&:hover': { bgcolor: 'action.hover' },
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
          }}
        >
          <Box
            aria-hidden
            sx={{
              width: 30, height: 30, borderRadius: 1.5, flexShrink: 0,
              display: 'grid', placeItems: 'center',
              bgcolor: failed ? 'error.main' : 'warning.main', color: 'common.white',
            }}
          >
            <BuildOutlinedIcon sx={{ fontSize: 17 }} />
          </Box>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {entry.name || 'tool'}
              </Typography>
              {failed
                ? <ErrorOutlineIcon sx={{ fontSize: 15, color: 'error.main' }} />
                : <CheckCircleIcon sx={{ fontSize: 15, color: 'success.main' }} />}
            </Box>
            <Typography
              variant="caption"
              sx={{ color: failed ? 'error.main' : 'success.dark', display: 'block' }}
            >
              {failed ? entry.failure : 'Completato con successo'}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ ...tabular, color: 'text.secondary', flexShrink: 0 }}>
            {clock(entry.time_ms)}
            {entry.duration_ms != null && ` · ${secs(entry.duration_ms)}`}
          </Typography>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Box>

        <Collapse in={open} unmountOnExit>
          <Box sx={{ px: 1.75, pb: 1.75 }}>
            {entry.args && (
              <>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Richiesta</Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0, mt: 0.5, mb: 1.5, p: 1.25, borderRadius: 1.5, fontSize: '0.78rem',
                    maxWidth: '100%', overflowX: 'auto',
                    bgcolor: 'grey.100', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  }}
                >
                  {entry.args}
                </Box>
              </>
            )}
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Risposta</Typography>
            <Box
              component="pre"
              sx={{
                m: 0, mt: 0.5, p: 1.25, borderRadius: 1.5, fontSize: '0.78rem',
                maxHeight: 280, maxWidth: '100%', overflow: 'auto',
                bgcolor: 'grey.100', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              }}
            >
              {entry.result || '(vuota)'}
            </Box>
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}

function Bubble({ entry }: { entry: TranscriptEntry }) {
  if (entry.kind === 'tool') return <ToolCard entry={entry} />;

  const isBot = entry.kind === 'bot';
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: isBot ? 'flex-start' : 'flex-end' }}>
        {/* Il tetto è la misura di lettura, non una percentuale: all'84% di
            un pannello largo le righe arrivavano a ~110 caratteri, ben oltre
            i 65-75 in cui l'occhio ritrova l'inizio della riga successiva. */}
        <Box sx={{ maxWidth: { xs: '100%', sm: 'min(84%, 68ch)' }, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block', mb: 0.5, fontWeight: 600,
              // Chi parla si riconosce dall'etichetta, non solo dal lato: su
              // schermo stretto le bolle si allineano quasi uguali.
              // Il chiamante è blu, non arancione: l'arancione in questa
              // pagina significa "problema" — lo porta il tool fallito — e
              // usarlo per un'etichetta neutra lo svuotava.
              color: isBot ? 'success.dark' : 'primary.dark',
              textAlign: isBot ? 'left' : 'right',
            }}
          >
            {isBot ? (entry.speaker || 'Assistente') : 'Chiamante'}
          </Typography>
          <Box
            sx={(theme) => ({
              px: 1.75, py: 1.25, borderRadius: 2,
              bgcolor: isBot ? theme.palette.grey[100] : alpha(theme.palette.primary.main, 0.06),
              border: 1,
              borderColor: isBot ? theme.palette.grey[300] : alpha(theme.palette.primary.main, 0.22),
            })}
          >
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
              {entry.text || <Box component="span" sx={{ color: 'text.disabled' }}>(nessun testo)</Box>}
            </Typography>
          </Box>
          <Stamp entry={entry} align={isBot ? 'left' : 'right'}>
            {isBot && entry.wait_ms != null && (
              <Tooltip
                arrow
                title="Dal momento in cui il chiamante ha smesso di parlare a questa risposta"
              >
                <Chip
                  size="small"
                  label={`atteso ${secs(entry.wait_ms)}`}
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem', cursor: 'help', ...tabular }}
                />
              </Tooltip>
            )}
          </Stamp>
        </Box>
      </Box>
    </Box>
  );
}

export default function CallDiagnostics({ callId }: { callId: string }) {
  const [data, setData] = useState<Diagnostics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Niente guard su useRef qui: in StrictMode l'effetto gira due volte, il
    // cleanup del primo passaggio mette `alive = false`, e un guard farebbe
    // saltare il secondo fetch — lo stato non verrebbe mai impostato e il
    // pannello resterebbe sullo scheletro per sempre. `alive` basta già a
    // scartare una risposta arrivata tardi, e `[callId]` a non rifare il
    // lavoro senza motivo.
    let alive = true;
    setLoading(true);
    setError(null);
    callService
      .getCallDiagnostics(callId)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [callId]);

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={220} />
        <Skeleton variant="rounded" height={64} sx={{ mt: 2, maxWidth: '78%' }} />
        <Skeleton variant="rounded" height={48} sx={{ mt: 2, ml: 'auto', maxWidth: '62%' }} />
        <Skeleton variant="rounded" height={64} sx={{ mt: 2, maxWidth: '78%' }} />
      </Box>
    );
  }

  if (error) return <Alert severity="error" variant="outlined">{error}</Alert>;

  if (!data || data.empty) {
    const days = data?.retention_days ?? 15;
    return (
      <Alert severity="info" variant="outlined">
        Registrazione della conversazione non disponibile. Viene conservata per {days} giorni
        insieme agli altri artifact della chiamata, e per una chiamata appena conclusa può non
        essere ancora stata scritta.
      </Alert>
    );
  }

  const transcript = data.transcript ?? [];
  const avg = data.averages;
  const lifecycle = data.lifecycle ?? [];
  const queued = lifecycle.find((e) => e.event === 'call.queued');
  const connected = lifecycle.find((e) => e.event === 'call.transportConnected');
  const connect = queued && connected ? connected.at_ms - queued.at_ms : null;

  return (
    <Box>
      {/* Riepilogo in una riga: la conversazione è il contenuto, questo la annota. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 0.75, mb: 1 }}>
        {connect != null && (
          <>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Connessione</Typography>
            <Typography
              variant="body2"
              sx={{ ...tabular, fontWeight: 600 }}
            >
              {secs(connect)}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.disabled', px: 0.5 }}>·</Typography>
          </>
        )}
        {data.duration_ms != null && (
          <>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Durata</Typography>
            <Typography variant="body2" sx={{ ...tabular, fontWeight: 600 }}>{secs(data.duration_ms)}</Typography>
          </>
        )}
        {data.ended_reason && (
          <>
            <Typography variant="body2" sx={{ color: 'text.disabled', px: 0.5 }}>·</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Chiusa da</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{data.ended_reason}</Typography>
          </>
        )}
      </Box>

      {avg && (
        <Typography variant="caption" sx={{ ...tabular, color: 'text.secondary', display: 'block', mb: 2 }}>
          Attesa media {secs(avg.total)} su {avg.turns} turni, peggiore {secs(avg.worst)} — di cui
          trascrizione {secs(avg.transcriber)}, fine turno {secs(avg.endpointing)},
          modello {secs(avg.model)}, voce {secs(avg.voice)}
          {avg.interrupted ? ` · ${avg.interrupted} interruzioni` : ''}
        </Typography>
      )}

      <Divider sx={{ mb: 2.5 }} />

      {transcript.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Nessuno scambio registrato: la chiamata si è chiusa prima che si parlasse.
        </Typography>
      ) : (
        transcript.map((e, i) => <Bubble key={i} entry={e} />)
      )}

    </Box>
  );
}
